import { useNavigate } from 'react-router-dom';
import { RowAction, TableParams } from '../types';
import { useRef } from 'react';
import { useGenericMutation } from '@/legacy/hooks/useGenericMutation';
import { buildRequestFromRecord, replaceParams } from '../utils';
import { queryClient } from '@/legacy/lib';
import { TABLE_CACHE_BYPASS_KEY } from '@/legacy/constants';

export const useHandleRowAction = <T extends Record<string, any>>(context?: {
	open?: (action: RowAction, selectedRecords: T[]) => void;
	setEditMode?: (edit: boolean) => void;
	tableId?: string;
	updateTableParams?: (params: Partial<TableParams>) => void;
	forceNextDbFetch?: () => void;
}) => {
	const navigate = useNavigate();

	const currentActionRef = useRef<RowAction | null>(null);
	const setCurrentAction = (action: RowAction) => {
		currentActionRef.current = action;
	};

	const { mutate } = useGenericMutation({
		onSuccess: (data) => {
			const currentAction = currentActionRef.current;
			if (currentAction?.onSuccess) {
				currentAction.onSuccess(data);
			}
			(currentAction?.invalidateQueries || []).forEach((key) => {
				queryClient.invalidateQueries({ queryKey: [key] });
				queryClient.setQueryData([TABLE_CACHE_BYPASS_KEY, key], Date.now());
			});

			if (currentAction?.nextTableParams && context?.updateTableParams) {
				context.updateTableParams(currentAction.nextTableParams);
				context.forceNextDbFetch?.();
			}
		},
		onError: () => {
			const currentAction = currentActionRef.current;
			if (currentAction?.onError) {
				currentAction.onError();
			}
		},
		successMessage: currentActionRef.current?.successMessage || '',
		errorMessage: currentActionRef.current?.errorMessage || '',
	});

	const handleToastAction = (action: RowAction, selectedRecords: T[], rowKey?: keyof T) => {
		setCurrentAction(action);
		const request = buildRequestFromRecord<T>({
			action,
			selectedRecords,
			rowKey,
		});
		mutate({
			endpoint: request.endpoint,
			method: action.method,
			payload: request.payload,
			responseType: action.responseType || 'json',
			actionId: action.actionId,
		});
	};

	const handleRowAction = (action: RowAction, selectedRecords: T[], rowKey?: keyof T) => {
		const first = selectedRecords[0];

		if (!first) {
			console.warn('handleRowAction called without any selected records');
			return;
		}

		// Custom action override
		if (action.action) {
			action.action(first, context, selectedRecords);
			return;
		}

		switch (action.type) {
			case 'redirect':
				if (action.to) {
					const finalUrl =
						typeof action.to === 'function' ? action.to(first) : replaceParams(action.to, first);
					navigate(finalUrl);
				}
				break;

			case 'toast':
				if (action.endpoint) {
					handleToastAction(action, selectedRecords, rowKey);
				}
				break;

			case 'confirm':
			case 'form':
			case 'customModal':
				if (context?.open) {
					console.log('Opening modal for action', action, 'with records', selectedRecords);
					context.open(action, selectedRecords);
				} else {
					console.warn('No modal handler connected for', action.type);
				}
				break;

			default:
				console.warn('Unknown action type', action);
		}
	};

	return handleRowAction;
};
